import React, { useEffect, useRef, useState } from 'react';
import { useHotelBookingGuestSiteTelemetry } from '../../HotelBooking.telemetry';
import { RedirectMechanismType } from '@graphql/generated-base';
import { RedirectWithSafeLink } from '@apps/guest/components/RedirectWithSafeLink';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';
import { useClientReference } from '@shared/hooks/useClientReference';
import { useParams } from '@react-router';
import { useCheckInCheckoutDates } from '@shared/hooks/useCheckInCheckoutDates';
import { RedirectInterceptorDialog } from '../../components/RedirectInterceptorDialog';
import type { GoogleMapsProps, TravelPlaceProps, PricingInfoProps, RedirectMechanismProps } from '../../HotelBooking.types';
import { PartnerHotelIdType } from '../../HotelBooking.types';
import { useHistory } from '@shared/core';
import { createSearchParamsFromAccommodationsPage, useHotelRoutePaths } from '@shared/utils/accommodations/hotelRoutes';
import { config } from '@static/js/env.config';
import { usePartnerHotelQueryParams } from '../../hooks/usePartnerHotelQueryParams';
import { useTravelWhiteLabelCurrency } from '@shared/hooks/useTravelWhiteLabelCurrency';
import { convertToJoyNativeCheckoutUrl, formatRoomOccupancyToString } from '@shared/utils/joyHotelUrl';
import type { WritableUtmParameters } from '@shared/utils/utmParameters';
import {
  convertToBERedirectUrl,
  convertToCustomOrHotelUrl,
  convertToNuiteeFallbackUrl,
  convertToRedirectTelemetry,
  convertToZhFallbackUrl,
  useDiscountCodeForRedirect,
} from './PartnerHotelRedirect.utils';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useHotelBookingRedirectHealth } from '../../HotelBooking.health';
import { withWindow } from '@shared/utils/withWindow';
import { useNuiteeLanguage } from '@shared/hooks/useNuiteeLanguage';
import type { XDataInput } from '@shared/utils/encodeData';
import { encodeData } from '@shared/utils/encodeData';
import { useRetainSupportedParamsInStaysRedirectUrl } from '@shared/hooks/useRetainSupportedParamsInStaysRedirectUrl';
import type { SearchParamsValue } from '@shared/components/SearchParamsObserver/SearchParamsObserver';
import { formatDateComponentsToString } from '@shared/utils/dateHelpers';
import { extractHotelRedirects } from '@shared/utils/extractHotelRedirects';

interface PartnerHotelRedirectProps
  extends Readonly<{
    pageContext: string;
    telemetryName: string;
    travelPlace: TravelPlaceProps;
    googleMaps?: GoogleMapsProps | null;
    pricingInfo?: PricingInfoProps | null;
    forceRedirectToHotelWebsite?: boolean;
    searchParams?: SearchParamsValue | null;
    redirectMechanisms?: RedirectMechanismProps[] | null;
    eventData?: { readonly eventId?: string | null; readonly eventDate?: string | null };
  }> {}

export const PartnerHotelRedirect = (props: PartnerHotelRedirectProps) => {
  const { pageContext, telemetryName, travelPlace, redirectMechanisms, googleMaps, pricingInfo, eventData, forceRedirectToHotelWebsite, searchParams } = props;
  const redirectInProgress = useRef(false);
  const [safeLink, setSafeLink] = useState<string | undefined>();
  const [finalRedirectTo, setFinalRedirectTo] = useState<'hotelPageUrl' | 'hotelSearchPageUrl'>();
  const [isOpenInterceptorDialog, setIsOpenInterceptorDialog] = useState(false);
  const { eventHandle } = useParams<{ eventHandle: string }>();

  const history = useHistory();
  const hotelRoute = useHotelRoutePaths();
  const { onLoadFailure, onLoadComplete } = useHotelBookingRedirectHealth();
  const { clientReference, loadingClientReference: loadingClientReferenceData } = useClientReference();
  const telemetry = useHotelBookingGuestSiteTelemetry();
  const hotelQueryParams = usePartnerHotelQueryParams(travelPlace.id);
  const language = useNuiteeLanguage();
  const { currency, loading: loadingCurrency } = useTravelWhiteLabelCurrency();
  const checkInCheckoutDates = useCheckInCheckoutDates({ eventDate: eventData?.eventDate });
  const { retainSupportedParamsInStaysRedirectUrl } = useRetainSupportedParamsInStaysRedirectUrl();

  // passed to nuitee (stays.withjoy.com)
  const nuiteeDiscountCode = useDiscountCodeForRedirect('nuitee', hotelQueryParams.joyHotelDiscountCode) || hotelQueryParams.discountCode;
  // used for native travel checkout
  const nativeDiscountCode = useDiscountCodeForRedirect('native', hotelQueryParams.joyHotelDiscountCode);

  const loadingData = loadingClientReferenceData || loadingCurrency;

  const handleOnAccept = useEventCallback(() => {
    setIsOpenInterceptorDialog(false);
    setFinalRedirectTo('hotelSearchPageUrl');
  });

  const handleOnDecline = useEventCallback(() => {
    setIsOpenInterceptorDialog(false);
    if (pageContext === 'AccommodationPlacePartner') {
      history.push(hotelRoute.accommodationsPage.buildRelativeUrl(eventHandle, createSearchParamsFromAccommodationsPage('partner_hotel_decline')));
    } else {
      setFinalRedirectTo('hotelPageUrl');
    }
  });

  useEffect(() => {
    if (redirectInProgress.current || loadingData) {
      return;
    }

    try {
      // Extract required info
      const { travelPlaceId, sourceType, source, utmParams, skipAvailabilityCheck, joyReferralCode, joyReferralRewardCode } = hotelQueryParams;

      const { hotelPageUrl, hotelSearchPageUrl } = extractHotelRedirects(redirectMechanisms);

      // Sold-out hotel: show the interceptor dialog (unless skipAvailabilityCheck routes straight to the hotel page).
      if (hotelSearchPageUrl && !finalRedirectTo && !forceRedirectToHotelWebsite && !skipAvailabilityCheck) {
        onLoadComplete();
        setIsOpenInterceptorDialog(true);
        return;
      }
      // Build xData from discountCode
      const xData = nuiteeDiscountCode ? encodeData<XDataInput>({ discount: { code: nuiteeDiscountCode } }) : undefined;

      const hasHotelSearchPageUrl = finalRedirectTo === 'hotelSearchPageUrl';
      const redirectData = hasHotelSearchPageUrl && hotelSearchPageUrl && !skipAvailabilityCheck ? hotelSearchPageUrl : hotelPageUrl;

      // Build telemetry payload -  Don't want break existing telemetry
      const telemetryPayload = convertToRedirectTelemetry({
        clientReference,
        redirectData,
        pricingInfo,
        googleMaps,
        telemetryName,
        source,
        utmParams,
        hasHotelSearchPageUrl: !!hotelSearchPageUrl,
      });

      // __A__ Redirect to customUrl(Couple own booking link) / hotelWebsite with eventredirect service.
      const hotelWebsite = forceRedirectToHotelWebsite ? travelPlace.customUrl || travelPlace.url || redirectData?.url : redirectData?.url;
      if ((redirectData?.type === RedirectMechanismType.custom || forceRedirectToHotelWebsite || hasHotelSearchPageUrl) && hotelWebsite) {
        const hotelWebsiteEnhanced = retainSupportedParamsInStaysRedirectUrl(hotelWebsite);
        const hotelUrl = convertToCustomOrHotelUrl(hotelWebsiteEnhanced, clientReference, currency, redirectData, hasHotelSearchPageUrl, hotelQueryParams.utmParams);
        telemetry.guestPartnerHotelBookingRedirect({
          destinationUrl: hotelUrl,
          ...telemetryPayload,
          onTelemetrySend: () => {
            onLoadComplete();
            setSafeLink(hotelUrl.toString());
          },
        });
        redirectInProgress.current = true;
        return;

        // __B__ Redirect to Joy native checkout
      } else if (redirectData?.type === RedirectMechanismType.joyNativeCheckout) {
        const roomsString = searchParams?.rooms && searchParams.rooms.valid ? searchParams.rooms.value.map(formatRoomOccupancyToString).join(',') : undefined;

        const checkIn = searchParams?.startDate?.valid ? formatDateComponentsToString(searchParams.startDate.value) : undefined;
        const checkOut = searchParams?.endDate?.valid ? formatDateComponentsToString(searchParams.endDate.value) : undefined;

        // The triplet stays all-or-nothing — a half-filled attribution would misreport the source.
        // `utm_content` is independent of it, matching the server interceptor: an inbound `utm_medium`
        // outside `UTM_MEDIUMS` is dropped on read, and the experiment arm must not go down with it.
        const { utm_medium, utm_source, utm_campaign, utm_source_secondary, utm_content } = utmParams ?? {};
        const hasCompleteTriplet = !!(utm_medium && utm_source && utm_campaign);
        const validUtmParams: WritableUtmParameters | undefined =
          hasCompleteTriplet || utm_content
            ? {
                ...(hasCompleteTriplet && { utm_medium, utm_source, utm_campaign }),
                ...(hasCompleteTriplet && utm_source_secondary && { utm_source_secondary }),
                ...(utm_content && { utm_content }),
              }
            : undefined;

        // Branch __A__ above hands its URL to `retainSupportedParamsInStaysRedirectUrl`; this one is
        // built from scratch, so the Share Your Stay codes have to be threaded in explicitly or they
        // stop here. Checkout is the only reader of either, and this branch is how a referred guest
        // arriving from hotel search reaches it, so a drop here costs them the discount their friend's
        // link promised and costs the friend the reward for sending them.
        const redirectUrl = convertToJoyNativeCheckoutUrl({
          checkIn,
          checkOut,
          currency,
          roomsString,
          url: redirectData.url,
          discountCode: nativeDiscountCode,
          referralCode: joyReferralCode,
          referralRewardCode: joyReferralRewardCode,
          utmParams: validUtmParams,
        });

        telemetry.guestPartnerHotelBookingRedirect({
          destinationUrl: redirectUrl,
          ...telemetryPayload,
          onTelemetrySend: () => {
            onLoadComplete({ url: redirectUrl, isExternalUrl: true });
          },
        });
        redirectInProgress.current = true;
        return;

        // __C__ Redirect BE redirect logic url
      } else if (redirectData?.url) {
        const redirectUrl = convertToBERedirectUrl({ redirectData, clientReference, currency, language, xData, placeId: googleMaps?.gplid, name: googleMaps?.name });
        telemetry.guestPartnerHotelBookingRedirect({
          destinationUrl: redirectUrl,
          ...telemetryPayload,
          onTelemetrySend: () => {
            onLoadComplete({ url: redirectUrl, isExternalUrl: true });
          },
        });
        redirectInProgress.current = true;
        return;

        // __D__ Fallback if don't have joyPlace for zh.
      } else if (travelPlaceId && travelPlace.type === 'joyPlace' && sourceType === PartnerHotelIdType.hotels) {
        const zhUrl = convertToZhFallbackUrl(travelPlaceId, clientReference, currency, checkInCheckoutDates);
        telemetry.guestPartnerHotelBookingRedirect({
          destinationUrl: zhUrl,
          ...telemetryPayload,
          destinationName: 'zentrumHub',
          onTelemetrySend: () => {
            onLoadComplete({ url: zhUrl, isExternalUrl: true });
          },
        });
        redirectInProgress.current = true;
        return;

        // __E__ Fallback if don't have joyPlace for nuitee.
      } else if (travelPlaceId && travelPlace.type === 'joyPlace' && sourceType === PartnerHotelIdType.stays) {
        const nuiteeUrl = convertToNuiteeFallbackUrl({
          xData,
          currency,
          language,
          clientReference,
          checkInCheckoutDates,
          name: googleMaps?.name,
          joyPlaceId: travelPlaceId,
          placeId: googleMaps?.gplid,
        });
        telemetry.guestPartnerHotelBookingRedirect({
          destinationUrl: nuiteeUrl,
          ...telemetryPayload,
          destinationName: 'externalPDPUrl',
          onTelemetrySend: () => {
            onLoadComplete({ url: nuiteeUrl, isExternalUrl: true });
          },
        });
        redirectInProgress.current = true;
        return;

        // __F__ Show 404 if not find any valid redirect url
      } else {
        telemetry.guestPartnerHotelBookingRedirect({
          clientReference,
          ...telemetryPayload,
          onTelemetrySend: () => {
            onLoadFailure(
              `${pageContext}: Partner hotel not found. ${!travelPlaceId ? 'Place Id Missing.' : ''} utm_campaign: ${hotelQueryParams.utmParams?.utm_campaign}`,
              undefined,
              { forceErrorUI: true },
            );
          },
        });

        redirectInProgress.current = true;
        return;
      }
    } catch (error) {
      const redirect = (() => {
        if (eventHandle) {
          return {
            url: hotelRoute.hotelSearch.buildRelativeUrl(eventHandle, { utm_medium: 'web', utm_source: 'hotel_details', utm_campaign: 'error_build_url_failed' }),
            isExternalUrl: false,
          };
        } else {
          return {
            url: config.nuiteeWhitelableBaseUrl,
            isExternalUrl: true,
          };
        }
      })();
      const destinationUrl = new URL(redirect.url, redirect.isExternalUrl ? undefined : withWindow(global => global.location.origin, ''));
      telemetry.guestPartnerHotelBookingRedirect({
        destinationUrl,
        clientReference,
        onTelemetrySend: () => {
          onLoadFailure(`${pageContext}: Failed to build redirect URL. utm_campaign: ${hotelQueryParams.utmParams?.utm_campaign}`, error as Error, redirect);
        },
      });
      redirectInProgress.current = true;
      return;
    }
  }, [
    checkInCheckoutDates,
    telemetry,
    clientReference,
    loadingData,
    language,
    currency,
    eventHandle,
    finalRedirectTo,
    googleMaps,
    redirectMechanisms,
    pricingInfo,
    travelPlace,
    history,
    hotelRoute,
    hotelQueryParams,
    telemetryName,
    forceRedirectToHotelWebsite,
    redirectInProgress,
    pageContext,
    onLoadFailure,
    onLoadComplete,
    retainSupportedParamsInStaysRedirectUrl,
    searchParams,
    nuiteeDiscountCode,
    nativeDiscountCode,
  ]);

  return (
    <>
      {!redirectInProgress.current && !isOpenInterceptorDialog && <JoyLogoLoader loaderKey="guest-hotel-booking-partner-hotel-redirect" />}
      <RedirectWithSafeLink
        href={safeLink}
        skipTelemetry
        showEmptyTabMessage
        target="_self"
        onClose={() => {
          setSafeLink(undefined);
        }}
      />
      <RedirectInterceptorDialog
        pageContext={pageContext}
        isOpen={isOpenInterceptorDialog}
        hotel={{
          hotelName: travelPlace.name,
          hotelPriceCurrency: pricingInfo?.nightlyBaseRate.currency,
          hotelPrice: pricingInfo?.nightlyBaseRate.floatingPointDecimalString,
          hotelStrikeOutPrice: pricingInfo?.strikeoutNightlyBaseRate?.floatingPointDecimalString,
        }}
        onAccept={handleOnAccept}
        onDecline={handleOnDecline}
      />
    </>
  );
};
